import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, findCountry, flagEmoji, isValidNationalNumber } from "@/data/phoneCountries";

/**
 * Shared "Phone Number" field — country-code dropdown + national number
 * input, with a regex dynamically tied to the selected country (see
 * data/phoneCountries.js). Used on both the Register and Contact pages so
 * validation stays byte-for-byte identical.
 *
 * The dropdown selects by unique ISO2 (not dial code) since several
 * countries share a dial code (e.g. US/Canada both +1) — Radix Select
 * requires unique item values or the trigger renders every matching label.
 */
export default function PhoneCountryInput({
    dialCode, nationalNumber, onDialChange, onNumberChange,
    testidPrefix = "phone", required = true, touched = false,
}) {
    const [iso2, setIso2] = useState(() => findCountry(dialCode)?.iso2 || COUNTRIES[0].iso2);
    const valid = !nationalNumber || isValidNationalNumber(dialCode, nationalNumber);

    // Keep iso2 in sync if a parent resets `dialCode` programmatically (e.g.
    // a form-reset after submit) — otherwise the trigger would silently
    // desync from the dial code actually used for validation.
    useEffect(() => {
        setIso2((cur) => {
            const curDial = COUNTRIES.find((c) => c.iso2 === cur)?.dial;
            if (curDial === dialCode) return cur;
            return findCountry(dialCode)?.iso2 || cur;
        });
    }, [dialCode]);

    const onIso2Change = (nextIso2) => {
        setIso2(nextIso2);
        const c = COUNTRIES.find((x) => x.iso2 === nextIso2);
        if (c) onDialChange(c.dial);
    };

    return (
        <div>
            <div className="flex gap-2">
                <Select value={iso2} onValueChange={onIso2Change}>
                    <SelectTrigger
                        className="w-[104px] shrink-0 text-[16px] h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80]"
                        data-testid={`${testidPrefix}-country-select`}
                    >
                        {/* Compact trigger display (flag + dial only) — full "flag Name (+dial)"
                            stays in the dropdown list. Explicit children override Radix's
                            default item-text mirroring, which previously clipped long
                            country names inside the 128px trigger. */}
                        <SelectValue placeholder="Code">
                            {iso2 ? `${flagEmoji(iso2)} ${dialCode}` : "Code"}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        {COUNTRIES.map((c) => (
                            <SelectItem key={c.iso2} value={c.iso2} data-testid={`${testidPrefix}-country-${c.iso2}`}>
                                <span className="mr-1.5">{flagEmoji(c.iso2)}</span>{c.name} ({c.dial})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <input
                    type="tel"
                    inputMode="numeric"
                    required={required}
                    placeholder="712345678"
                    value={nationalNumber}
                    onChange={(e) => onNumberChange(e.target.value.replace(/\D/g, "").slice(0, 15))}
                    data-testid={`${testidPrefix}-national-number`}
                    className="flex-1 h-11 rounded-lg border border-input bg-transparent px-3 text-[16px] leading-relaxed outline-none focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80] transition-colors"
                />
            </div>
            {touched && nationalNumber && !valid && (
                <p className="text-xs text-destructive mt-1" data-testid={`${testidPrefix}-error`}>
                    Enter a valid number for the selected country code.
                </p>
            )}
        </div>
    );
}
